/**
 * VehiclePage.jsx — Drive Log + Mileage Summary.
 */

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton,
  Stack, Button, Chip, Select, MenuItem, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import {
  Sync as SyncIcon,
  DirectionsCar as CarIcon,
  Circle as DotIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTessieStatus, useTessieSync } from '../hooks/useTessie';
import { useMobile } from '../hooks/useMobile';
import { useAuth } from '../hooks/useAuth';
import { useRoster } from '../hooks/useRoster';
import { useRBAC } from '../hooks/useRBAC';
import DriveLog from '../components/vehicle/DriveLog';
import MileageSummary from '../components/vehicle/MileageSummary';
import VehicleSettingsModal from '../components/vehicle/VehicleSettingsModal';

const TABS = [
  { key: 'drives', label: 'Drive Log' },
  { key: 'summary', label: 'Mileage Summary' },
];

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function VehiclePage() {
  const [tab, setTab] = useState('drives');
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState('all');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isMobile } = useMobile();

  const { profile } = useAuth();
  const { isSuperAdmin } = useRBAC();
  const { data: roster = [] } = useRoster();
  const { data: status } = useTessieStatus();
  const syncMut = useTessieSync();

  const myRosterId = useMemo(() => {
    if (!profile?.id) return null;
    const r = roster.find((x) => x.profileId === profile.id || x.profile_id === profile.id);
    return r?.id || null;
  }, [profile, roster]);

  // Sidebar flyout deep link (?tab=drives|summary)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((x) => x.key === t)) { setTab(t); searchParams.delete('tab'); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const connected = status?.connected;
  const vehicleStatus = status?.vehicleStatus;
  const lastSync = status?.lastSync;
  const unreviewed = status?.unreviewedCount ?? 0;

  const effectiveScope = myRosterId ? scope : 'all';

  return (
    <Box sx={{ p: isMobile ? 1 : 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        sx={{ mb: 2, gap: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <CarIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>Vehicle</Typography>
          {connected !== undefined && (
            <Chip
              icon={<DotIcon sx={{ fontSize: '12px !important' }} />}
              label={connected ? (vehicleStatus === 'awake' ? 'Awake' : 'Asleep') : 'Disconnected'}
              size="small"
              color={connected ? 'success' : 'error'}
              variant="outlined"
            />
          )}
          {unreviewed > 0 && (
            <Chip size="small" label={`${unreviewed} unreviewed`} color="warning" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {lastSync && (
            <Typography variant="caption" color="text.secondary">
              Synced {timeAgo(lastSync)}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={syncMut.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending || !connected}
            sx={{ minHeight: 36 }}
          >
            {syncMut.isPending ? 'Syncing…' : 'Sync Now'}
          </Button>
          <Tooltip title="Vehicle settings">
            <IconButton size="small" onClick={() => setSettingsOpen(true)} aria-label="Vehicle settings" sx={{ display: isSuperAdmin ? 'inline-flex' : 'none' }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <VehicleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {syncMut.isSuccess && syncMut.data && (
        <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
          Synced: {syncMut.data.inserted} new, {syncMut.data.updated} updated, {syncMut.data.skipped} unchanged
          {syncMut.data.suggestions?.suggested > 0
            ? ` · ${syncMut.data.suggestions.suggested} driver suggestion${syncMut.data.suggestions.suggested === 1 ? '' : 's'}`
            : ''}
        </Typography>
      )}
      {syncMut.isError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Sync failed: {syncMut.error?.message || 'Unknown error'}
        </Typography>
      )}

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.25}
        sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
      >
        {myRosterId && tab === 'drives' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={effectiveScope}
            onChange={(_, v) => v && setScope(v)}
            fullWidth={isMobile}
          >
            <ToggleButton value="all" sx={{ minHeight: 36, flex: isMobile ? 1 : 'unset' }}>
              All Drives
            </ToggleButton>
            <ToggleButton value="mine" sx={{ minHeight: 36, flex: isMobile ? 1 : 'unset' }}>
              My Drives
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Box sx={{ flex: 1 }} />

        {isMobile ? (
          <Select value={tab} onChange={(e) => setTab(e.target.value)} size="small" fullWidth>
            {TABS.map((t) => (<MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>))}
          </Select>
        ) : (
          <ToggleButtonGroup value={tab} exclusive size="small" onChange={(_, v) => v && setTab(v)}>
            {TABS.map((t) => (<ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>))}
          </ToggleButtonGroup>
        )}
      </Stack>

      {tab === 'drives' && (
        <DriveLog myRosterId={effectiveScope === 'mine' ? myRosterId : null} />
      )}
      {tab === 'summary' && <MileageSummary />}
    </Box>
  );
}
